import { Debounced } from 'runed'
import Fuse from 'fuse.js'
import { calculateFromQuery } from '@lib/utils'

export function useList({ 
	onQuery,
	onSelect,
	onEscape,
	onClear,
	indexStart = 0, // Set this to -1 to NOT have the first item auto selected
	delay = 250,
	minLength = 2,
  useFuse = false,
  fuseOptions = { includeScore: true, threshold: 0.2, keys: ['label', 'name'] }
}) {

	let index = $state(indexStart)
  let refs = $state([]) // keep identity stable
	let query = $state("")
	let items = $state([])
	let loading = $state(false) // Makes available a loading state that can be updated from the parent when calling onQuery
  let navigating = $state(false)
  let requestId = 0

  let typing = $derived(query.length && debouncedQuery?.pending)

  let highlighted = $derived(items[index])

  let queryCalculation = $derived(calculateFromQuery(query))

  let selectedItem = $derived(items[index])

  let message = $derived.by(() => {
    if (typing && query.length >= minLength) return 'En train de taper...'
    if (!query.length) return 'Entrez un terme pour commencer...'
    if (query.length < minLength) return `Rechercher au moins ${minLength} caractères`
    if (loading) return `Recherche en cours pour "${query}"`
    if (queryCalculation) return `Résultat de la calcul: ${queryCalculation}`
    if (items.length === 0) return 'Aucun résultat trouvé'
    return null
  })

  let showResults = $derived(items.length > 0 && !typing && !loading && !queryCalculation)

  let showHintText = $derived(!typing && !loading && !queryCalculation && !query.length)

	const debouncedQuery = onQuery ? new Debounced(() => query, delay) : null

  // reset index when indexStart changes
  // $effect(() => { index = indexStart })

  // keep length in sync with items changes (identity preserved)
  $effect(() => {
    refs.length = items.length
  })

  // scroll when index changes
  $effect(() => {
    if (index >= 0) refs[index]?.scrollIntoView?.({ behavior: "instant", block: "nearest" })
  })

	$effect(async () => {
		// Will run whenever debouncedQuery.current changes (eg after delay) and is of minLength
		if (onQuery && (debouncedQuery.current.length >= minLength) && !queryCalculation) {
      const currentRequestId = ++requestId
      const currentQuery = debouncedQuery.current

			loading = true
      try {
        const data = await onQuery(currentQuery)

        if (currentRequestId !== requestId) {
          return
        }

        if (currentQuery !== query) {
          return
        }

        if (useFuse) {
          const fuse = new Fuse(data, fuseOptions)
          items = fuse.search(currentQuery).map(result => result.item)
        } else {
          items = data
        }

        index = indexStart
      } catch (e) {
        // Abort is expected when the user types quickly; treat it like "no results yet"
        if (e?.name === 'AbortError') {
          if (currentRequestId !== requestId) return

          items = []
          index = indexStart
          return
        }
        if (currentRequestId !== requestId) return

        console.error('useList onQuery error', e)
        items = []
        index = indexStart
      } finally {
        if (currentRequestId === requestId) loading = false
      }
		}
	})

	// Handle the special case where if a query is less than minLength then we want to immediately clear, not wait
	$effect(() => {
		if (!items.length) return // If there are no items, we don't need to send a clear event or reset the items
		if (query.length < minLength) {
			items = []
      index = indexStart
			onClear?.()
		}
	})

	function handleUpKey(e) {
    e?.preventDefault()
    e?.stopPropagation()
    if (e?.shiftKey) {
      index = indexStart
    } else {
      index = Math.max(index - 1, indexStart)
    }
  }

  function handleDownKey(e) {
    e?.preventDefault()
    e?.stopPropagation()
		if (!items.length) return
    if (e?.shiftKey) {
      index = items.length - 1
    } else {
      index = Math.min(index + 1, items.length - 1)
    }
  }

	function handleEnterKey(e) {
    if (!items.length || loading || typing) return
		e?.preventDefault()
		e?.stopPropagation()
    if (index === -1) return
		onSelect?.({ index, item: items[index]})
	}

  function selectItem(idx) {
    index = idx
    onSelect?.({ index, item: selectedItem })
  }

	function handleEscapeKey() {
		onEscape?.()
	}

	function onkeydown(e) {
    if (e.key === "ArrowUp") {
      handleUpKey(e)
    } else if (e.key === "ArrowDown") {
      handleDownKey(e)
    } else if (e.key === "Enter") {
      handleEnterKey(e)
    } else if (e.key === "Escape") {
      handleEscapeKey(e)
    }
	}

	return {
		get query() { return query },
		set query(value) { query = value },
		get items() { return items },
		set items(value) { items = value },
		get loading() { return loading },
    set loading(value) { [true, false].includes(value) && (loading = value) },
    get navigating() { return navigating },
    set navigating(value) { [true, false].includes(value) && (navigating = value) },
		get index() { return index },
    set index(value) { index = value },
    get selectedItem() { return selectedItem },
    get message() { return message },
		get refs() { return refs },
		get typing() { return typing },
    get highlighted() { return highlighted },
    get showResults() { return showResults },
    get showHintText() { return showHintText },
    get queryCalculation() { return queryCalculation },
    get up() { return handleUpKey },
    get down() { return handleDownKey },
    get enter() { return handleEnterKey },
		onkeydown,
    selectItem
	}
	
}
